/**
 * Power Automate Connector Metadata API client
 */

import { requestWithRetry } from "../utils/http.js";
import type { AuthProvider } from "../auth/types.js";
import type { Connector, Connection, ApiListResponse, SwaggerDefinition, SwaggerParameter } from "./types.js";
import { createChildLogger } from "../utils/logger.js";
import {
  ConnectorNotFoundError,
  ApiError,
  RateLimitError,
  AuthorizationError,
  wrapApiCall,
} from "../utils/errors.js";
import { POWERAPPS_API_BASE, POWERAPPS_API_VERSION } from "../utils/constants.js";
import { escapeOData, validateEnvironmentId, validateGraphId } from "../utils/security.js";
import { boundedConnectorSchema, connectorAsyncExtensions } from "../utils/connector-schema.js";

const apiLogger = createChildLogger({ module: "connector-api" });

const CONNECTIONS_PATH = "/providers/Microsoft.PowerApps/connections";
const CONNECTION_LIST_MAX_PAGES = 20;
const CONNECTION_LIST_MAX_ROWS = 10_000;
const CONNECTION_LIST_MAX_PAGE_BYTES = 4 * 1024 * 1024;
const CONNECTION_LIST_MAX_BYTES = 16 * 1024 * 1024;

function incompleteConnectionList(reason: string): ApiError {
  return new ApiError(`List connections incomplete: ${reason}; no partial result returned.`, 502);
}

/** Never forward the Power Apps bearer to a different collection or environment. */
function connectionContinuation(value: unknown, environmentFilter: string): string {
  if (typeof value !== "string" || value.length === 0 || value.length > 8192 || /[\s\\#]/.test(value)) {
    throw incompleteConnectionList("unsupported continuation link");
  }
  let url: URL;
  try { url = new URL(value); } catch { throw incompleteConnectionList("invalid continuation link"); }
  if (url.origin !== POWERAPPS_API_BASE || url.username || url.password ||
      url.pathname !== CONNECTIONS_PATH || url.hash ||
      url.searchParams.getAll("api-version").length !== 1 ||
      url.searchParams.get("api-version") !== POWERAPPS_API_VERSION ||
      url.searchParams.getAll("$filter").length !== 1 ||
      url.searchParams.get("$filter") !== environmentFilter) {
    throw incompleteConnectionList("continuation did not preserve the fixed origin, collection and environment");
  }
  // Sorting only the key/value pairs detects a loop disguised by query ordering.
  // The actual request retains the service's validated continuation URL unchanged.
  return url.href;
}

function connectionPageKey(value: string): string {
  const url = new URL(value);
  url.searchParams.sort();
  return url.href;
}

export interface ConnectorMetadataApiConfig {
  authProvider: AuthProvider;
  environmentId: string;
}

/**
 * Client for Power Apps Connector Metadata API
 */
export class ConnectorMetadataApi {
  private readonly authProvider: AuthProvider;
  private readonly environmentId: string;

  constructor(config: ConnectorMetadataApiConfig) {
    this.authProvider = config.authProvider;
    // Validate environment ID to prevent injection attacks
    this.environmentId = validateEnvironmentId(config.environmentId);
  }

  /**
   * Scope one call without mutating the session's client. Custom-connector
   * reads, writes, and update readbacks must use the same validated target.
   * Name resolution happens at MCP dispatch; direct callers pass an ID.
   */
  forEnvironment(environmentId: string): ConnectorMetadataApi {
    return new ConnectorMetadataApi({ authProvider: this.authProvider, environmentId });
  }

  /**
   * Get authorization header with bearer token
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.authProvider.getTokenForResource(
      "https://service.powerapps.com"
    );
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  /**
   * Build API URL for connector operations
   */
  private buildUrl(
    path: string,
    queryParams?: Record<string, string | undefined>
  ): string {
    const base = `${POWERAPPS_API_BASE}${path}`;
    const params = new URLSearchParams();
    params.set("api-version", POWERAPPS_API_VERSION);

    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        if (value !== undefined) {
          params.set(key, value);
        }
      }
    }

    return `${base}?${params.toString()}`;
  }

  /**
   * Handle API response errors
   */
  private async handleResponse<T>(
    response: Awaited<ReturnType<typeof requestWithRetry>>,
    context: string,
    options: { allowEmpty?: boolean } = {}
  ): Promise<T> {
    const { statusCode, body } = response;

    if (statusCode === 429) {
      await body.text();
      const retryAfter = response.headers["retry-after"];
      const retryMs = retryAfter ? parseInt(String(retryAfter), 10) * 1000 : undefined;
      throw new RateLimitError(retryMs);
    }

    // Stream-read body with size limit to prevent OOM on large responses
    const MAX_RESPONSE_SIZE = 50 * 1024 * 1024; // 50MB
    const chunks: Buffer[] = [];
    let totalSize = 0;
    for await (const chunk of body as AsyncIterable<Uint8Array>) {
      totalSize += chunk.length;
      if (totalSize > MAX_RESPONSE_SIZE) {
        throw new ApiError(`${context}: response too large (max 50MB)`, 413);
      }
      chunks.push(Buffer.from(chunk));
    }
    const responseText = Buffer.concat(chunks).toString("utf-8");

    if (statusCode === 401) {
      throw new AuthorizationError(`${context}: Authentication failed`);
    }

    if (statusCode === 403) {
      throw new AuthorizationError(`${context}: Access denied`);
    }

    if (statusCode === 404) {
      throw new ConnectorNotFoundError(context);
    }

    if (statusCode >= 400) {
      let errorDetails: unknown;
      try {
        errorDetails = JSON.parse(responseText);
      } catch {
        errorDetails = responseText;
      }
      throw new ApiError(`${context}: API error`, statusCode, errorDetails);
    }

    // A successful PATCH answers with an empty body; JSON.parse("") would turn
    // a completed update into "Unexpected end of JSON input".
    if (options.allowEmpty && responseText.trim() === "") {
      return null as T;
    }
    return JSON.parse(responseText) as T;
  }

  /**
   * List all connectors in the environment
   */
  async listConnectors(): Promise<Connector[]> {
    apiLogger.debug({ environmentId: this.environmentId }, "Listing connectors");

    const url = this.buildUrl("/providers/Microsoft.PowerApps/apis", {
      $filter: `environment eq '${escapeOData(this.environmentId)}'`,
    });
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<Connector>>(
        response,
        "List connectors"
      );
      apiLogger.info({ count: data.value.length }, "Listed connectors");
      return data.value;
    }, "List connectors");
  }

  /**
   * Get connector details with swagger definition
   */
  async getConnector(connectorId: string, includeSwagger = true): Promise<Connector> {
    validateGraphId(connectorId, "connector");
    apiLogger.debug({ connectorId }, "Getting connector");

    const url = this.buildUrl(`/providers/Microsoft.PowerApps/apis/${connectorId}`, {
      $filter: `environment eq '${escapeOData(this.environmentId)}'`,
      $expand: includeSwagger ? "swagger" : undefined,
    });
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const connector = await this.handleResponse<Connector>(response, connectorId);
      apiLogger.info(
        { connectorId, displayName: connector.properties.displayName },
        "Retrieved connector"
      );
      return connector;
    }, `Get connector ${connectorId}`);
  }

  /**
   * Get connector swagger definition
   */
  async getConnectorSwagger(connectorId: string): Promise<SwaggerDefinition | null> {
    const connector = await this.getConnector(connectorId, true);
    return connector.properties.swagger ?? null;
  }

  /**
   * Search connectors by name (client-side filtering)
   */
  async searchConnectors(query: string, limit = 20): Promise<Connector[]> {
    const allConnectors = await this.listConnectors();
    const lowerQuery = query.toLowerCase();

    const matches = allConnectors.filter((c) => {
      const name = c.properties.displayName.toLowerCase();
      const desc = (c.properties.description ?? "").toLowerCase();
      return name.includes(lowerQuery) || desc.includes(lowerQuery);
    });

    // Sort by relevance (name match first, then description match)
    matches.sort((a, b) => {
      const aNameMatch = a.properties.displayName.toLowerCase().includes(lowerQuery);
      const bNameMatch = b.properties.displayName.toLowerCase().includes(lowerQuery);
      if (aNameMatch && !bNameMatch) return -1;
      if (!aNameMatch && bNameMatch) return 1;
      return a.properties.displayName.localeCompare(b.properties.displayName);
    });

    return matches.slice(0, limit);
  }

  /**
   * Get connectors by tier
   */
  async getConnectorsByTier(tier: "Standard" | "Premium"): Promise<Connector[]> {
    const allConnectors = await this.listConnectors();
    return allConnectors.filter((c) => c.properties.tier === tier);
  }

  /**
   * Get connector actions (from swagger)
   */
  async getConnectorActions(connectorId: string): Promise<Array<{
    operationId: string;
    method: string;
    path: string;
    summary: string | null;
    description: string | null;
    isTrigger: boolean;
  }>> {
    const swagger = await this.getConnectorSwagger(connectorId);
    if (!swagger) {
      return [];
    }

    const actions: Array<{
      operationId: string;
      method: string;
      path: string;
      summary: string | null;
      description: string | null;
      isTrigger: boolean;
    }> = [];

    for (const [path, pathItem] of Object.entries(swagger.paths)) {
      for (const [method, operation] of Object.entries(pathItem)) {
        if (typeof operation === "object" && operation.operationId) {
          actions.push({
            operationId: operation.operationId,
            method: method.toUpperCase(),
            path,
            summary: operation.summary ?? null,
            description: operation.description ?? null,
            isTrigger: operation["x-ms-trigger"] !== undefined,
          });
        }
      }
    }

    return actions;
  }

  /**
   * Resolve an operation's parameters the way a swagger consumer must:
   * path-level parameters apply to every operation on the path, operation-level
   * parameters override them on (name, in) collision, and any entry may be a
   * $ref into the document's top-level parameters map. Connector swaggers
   * (Excel Online especially) lean heavily on $refs — returning them raw is
   * what rendered every parameter as "undefined" in get_action_schema.
   */
  private resolveOperationParameters(
    swagger: SwaggerDefinition,
    pathLevel: SwaggerParameter[] | undefined,
    operationLevel: SwaggerParameter[] | undefined,
  ): SwaggerParameter[] {
    const resolveRef = (p: SwaggerParameter): SwaggerParameter => {
      if (!p.$ref) return p;
      const match = /^#\/parameters\/(.+)$/.exec(p.$ref);
      const refName = match?.[1];
      const resolved = refName ? swagger.parameters?.[refName] : undefined;
      // An unresolvable ref still gets a readable name instead of undefined.
      return resolved ?? { name: p.$ref, in: "query", description: "(unresolved $ref)" };
    };
    const merged = new Map<string, SwaggerParameter>();
    for (const list of [pathLevel ?? [], operationLevel ?? []]) {
      for (const raw of list) {
        const p = resolveRef(raw);
        merged.set(`${p.in}:${p.name}`, this.inlineBodySchema(swagger, p));
      }
    }
    return [...merged.values()];
  }

  /**
   * A body parameter usually points at `#/definitions/X`; returned raw, the
   * caller sees "emailMessage (required) - body" and nothing about To,
   * Subject or Body. Inline the definition (nested refs up to a few levels,
   * cycles cut) so the schema tool can list the fields that actually matter.
   */
  private inlineBodySchema(swagger: SwaggerDefinition, p: SwaggerParameter): SwaggerParameter {
    const schema = (p as { schema?: unknown }).schema;
    if (p.in !== "body" || !schema || typeof schema !== "object") return p;
    const definitions = (swagger.definitions ?? {}) as Record<string, unknown>;
    const resolve = (node: unknown, depth: number, seen: Set<string>): unknown => {
      if (!node || typeof node !== "object" || depth > 4) return node;
      if (Array.isArray(node)) return node.map((n) => resolve(n, depth + 1, seen));
      const rec = node as Record<string, unknown>;
      const ref = typeof rec["$ref"] === "string" ? (rec["$ref"]) : undefined;
      if (ref) {
        const match = /^#\/definitions\/(.+)$/.exec(ref);
        const name = match?.[1];
        if (!name || seen.has(name) || definitions[name] === undefined) return rec;
        const next = new Set(seen);
        next.add(name);
        return resolve(definitions[name], depth + 1, next);
      }
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(rec)) {
        out[k] = k === "properties" && v && typeof v === "object" && !Array.isArray(v)
          ? Object.fromEntries(Object.entries(v as Record<string, unknown>).map(([pk, pv]) => [pk, resolve(pv, depth + 1, seen)]))
          : k === "items" || k === "additionalProperties"
            ? resolve(v, depth + 1, seen)
            : v;
      }
      return out;
    };
    return { ...p, schema: resolve(schema, 0, new Set()) } as SwaggerParameter;
  }

  /**
   * Get action schema for a specific operation
   */
  async getActionSchema(connectorId: string, operationId: string): Promise<{
    operationId: string;
    method: string;
    path: string;
    summary: string | null;
    description: string | null;
    parameters: unknown[] | null;
    responses: Record<string, unknown> | null;
    extensions?: Record<string, unknown>;
    pathExtensions?: Record<string, unknown>;
  } | null> {
    const swagger = await this.getConnectorSwagger(connectorId);
    if (!swagger) {
      return null;
    }

    for (const [path, pathItem] of Object.entries(swagger.paths)) {
      // Swagger allows a path-level "parameters" array beside the method keys.
      const pathLevelParams = (pathItem as Record<string, unknown>)["parameters"] as
        | SwaggerParameter[]
        | undefined;
      for (const [method, operation] of Object.entries(pathItem)) {
        if (method === "parameters") continue;
        if (typeof operation === "object" && operation.operationId === operationId) {
          return {
            operationId: operation.operationId,
            method: method.toUpperCase(),
            path,
            summary: operation.summary ?? null,
            description: operation.description ?? null,
            parameters: this.resolveOperationParameters(
              swagger,
              Array.isArray(pathLevelParams) ? pathLevelParams : undefined,
              operation.parameters,
            ),
            responses: operation.responses
              ? boundedConnectorSchema(operation.responses, swagger.definitions, { preserveRootKeys: true }) as Record<string, unknown>
              : null,
            ...(connectorAsyncExtensions(operation, swagger.definitions)
              ? { extensions: connectorAsyncExtensions(operation, swagger.definitions) }
              : {}),
            ...(connectorAsyncExtensions(pathItem, swagger.definitions)
              ? { pathExtensions: connectorAsyncExtensions(pathItem, swagger.definitions) }
              : {}),
          };
        }
      }
    }

    return null;
  }

  // ==========================================================================
  // Connection Management (via PowerApps API)
  // ==========================================================================

  /**
   * List all connections in an environment.
   *
   * `environmentId` defaults to the configured environment. It must be honoured:
   * connections are environment-scoped, so listing without it silently answers about
   * the wrong environment. Field report: `list_connections({environment: <prod>})`
   * returned the DEFAULT environment's 82 connections, which read as "Jira is already
   * connected in Production" when Production in fact had none.
   */
  async listConnections(environmentId?: string): Promise<Connection[]> {
    const targetEnvironment = validateEnvironmentId(environmentId ?? this.environmentId);
    apiLogger.debug({ environmentId: targetEnvironment }, "Listing connections");

    const environmentFilter = `environment eq '${escapeOData(targetEnvironment)}'`;
    const firstUrl = this.buildUrl(CONNECTIONS_PATH, {
      $filter: environmentFilter,
    });
    const headers = await this.getAuthHeaders();
    // The HTTP sequence is bounded independently of the existing auth provider.
    const signal = AbortSignal.timeout(30_000);
    const rows: Connection[] = [];
    const seen = new Set<string>();
    let totalBytes = 0;
    let url = firstUrl;
    for (let page = 0; page < CONNECTION_LIST_MAX_PAGES; page += 1) {
      const key = connectionPageKey(url);
      if (seen.has(key)) throw incompleteConnectionList("continuation loop detected");
      seen.add(key);
      let response: Awaited<ReturnType<typeof requestWithRetry>>;
      try {
        response = await requestWithRetry(url, { method: "GET", headers, signal }, { maxAttempts: 1 });
      } catch {
        throw incompleteConnectionList("HTTP request failed or exceeded its deadline");
      }
      if (response.statusCode !== 200) {
        try { response.body.destroy(); } catch { /* Cleanup must not expose provider errors. */ }
        // Never expose an error body or follow redirects.
        if (response.statusCode === 401 || response.statusCode === 403) {
          throw new AuthorizationError("List connections incomplete: access denied; no partial result returned.");
        }
        if (response.statusCode === 429) {
          const error = new RateLimitError();
          error.message = "List connections incomplete: rate limited; no partial result returned.";
          throw error;
        }
        throw incompleteConnectionList(`HTTP ${response.statusCode}`);
      }
      const chunks: Buffer[] = [];
      let pageBytes = 0;
      try {
        for await (const chunk of response.body as AsyncIterable<Uint8Array>) {
          pageBytes += chunk.length;
          totalBytes += chunk.length;
          if (pageBytes > CONNECTION_LIST_MAX_PAGE_BYTES || totalBytes > CONNECTION_LIST_MAX_BYTES) {
            throw incompleteConnectionList("response byte limit exceeded");
          }
          chunks.push(Buffer.from(chunk));
        }
      } catch {
        try { response.body.destroy(); } catch { /* Retain the fixed bounded-read error. */ }
        throw incompleteConnectionList("response body exceeded bounds or could not be read");
      }
      let data: unknown;
      try { data = JSON.parse(Buffer.concat(chunks).toString("utf8")); }
      catch { throw incompleteConnectionList("invalid JSON response"); }
      if (data === null || typeof data !== "object" || Array.isArray(data) ||
          !Array.isArray((data as { value?: unknown }).value)) {
        throw incompleteConnectionList("invalid page shape");
      }
      const record = data as { value: unknown[]; nextLink?: unknown; "@odata.nextLink"?: unknown };
      if (record.value.some((row) => row === null || typeof row !== "object" || Array.isArray(row))) {
        throw incompleteConnectionList("invalid connection row");
      }
      if (rows.length + record.value.length > CONNECTION_LIST_MAX_ROWS) {
        throw incompleteConnectionList("connection row limit exceeded");
      }
      rows.push(...record.value as Connection[]);
      const links = [record.nextLink, record["@odata.nextLink"]].filter((value) => value !== undefined && value !== null);
      if (links.length === 0) {
        apiLogger.info({ count: rows.length, pages: page + 1 }, "Listed connections");
        return rows;
      }
      if (links.length > 1 && links[0] !== links[1]) throw incompleteConnectionList("conflicting continuation links");
      url = connectionContinuation(links[0], environmentFilter);
      if (rows.length === CONNECTION_LIST_MAX_ROWS) throw incompleteConnectionList("connection row limit reached with more pages");
    }
    throw incompleteConnectionList("page limit exceeded");
  }

  /**
   * Get a single connection by ID
   */
  async getConnection(connectionId: string): Promise<Connection> {
    apiLogger.debug({ connectionId }, "Getting connection");

    const url = this.buildUrl(`/providers/Microsoft.PowerApps/connections/${connectionId}`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const connection = await this.handleResponse<Connection>(response, connectionId);
      apiLogger.info(
        { connectionId, displayName: connection.properties.displayName },
        "Retrieved connection"
      );
      return connection;
    }, `Get connection ${connectionId}`);
  }

  // ==========================================================================
  // Custom Connector Management
  // ==========================================================================

  /**
   * List all custom connectors in the environment
   */
  async listCustomConnectors(): Promise<CustomConnector[]> {
    apiLogger.debug({ environmentId: this.environmentId }, "Listing custom connectors");

    const url = this.buildUrl("/providers/Microsoft.PowerApps/apis", {
      $filter: `environment eq '${escapeOData(this.environmentId)}' and IsCustomApi eq 'True'`,
    });
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<CustomConnector>>(
        response,
        "List custom connectors"
      );
      apiLogger.info({ count: data.value.length }, "Listed custom connectors");
      return data.value;
    }, "List custom connectors");
  }

  /**
   * Get a custom connector by ID
   */
  async getCustomConnector(connectorId: string): Promise<CustomConnector> {
    validateGraphId(connectorId, "connector");
    apiLogger.debug({ connectorId }, "Getting custom connector");

    const url = this.buildUrl(`/providers/Microsoft.PowerApps/apis/${connectorId}`, {
      $filter: `environment eq '${escapeOData(this.environmentId)}'`,
      $expand: "swagger",
    });
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const connector = await this.handleResponse<CustomConnector>(response, connectorId);
      apiLogger.info(
        { connectorId, displayName: connector.properties.displayName },
        "Retrieved custom connector"
      );
      return connector;
    }, `Get custom connector ${connectorId}`);
  }

  /**
   * Create a new custom connector
   */
  async createCustomConnector(definition: CustomConnectorDefinition): Promise<CustomConnector> {
    apiLogger.debug({ displayName: definition.properties.displayName }, "Creating custom connector");

    const url = this.buildUrl("/providers/Microsoft.PowerApps/apis", {
      $filter: `environment eq '${escapeOData(this.environmentId)}'`,
    });
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "POST",
        headers,
        body: JSON.stringify(serializeConnectorDefinition(definition, this.environmentId)),
      });
      const connector = await this.handleResponse<CustomConnector>(response, "Create custom connector");
      apiLogger.info(
        { connectorId: connector.name, displayName: connector.properties.displayName },
        "Created custom connector"
      );
      return connector;
    }, "Create custom connector");
  }

  /**
   * The OpenAPI document as it was UPLOADED, not as the platform rewrote it.
   *
   * `properties.swagger` on a GET is the platform's rewritten copy: host is
   * the APIM proxy, basePath is `/apim/<connector>`, every path gains a
   * `/{connectionId}` prefix and MCP operations get a generated GET twin.
   * Writing that back would point the connector at its own proxy. The API
   * exposes the original under `apiDefinitions.originalSwaggerUrl` (a short-
   * lived SAS link); returns null if there is none or it cannot be fetched,
   * so callers can fall back deliberately.
   */
  async getCustomConnectorOriginalDefinition(connector: CustomConnector): Promise<OpenApiSpec | null> {
    const url = connector.properties.apiDefinitions?.originalSwaggerUrl;
    if (!url) return null;
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      return null;
    }
    if (parsed.protocol !== "https:" || !parsed.hostname.endsWith(".blob.core.windows.net")) {
      apiLogger.warn({ host: parsed.hostname }, "Refusing to fetch original OpenAPI document from an unexpected host");
      return null;
    }
    try {
      const response = await requestWithRetry(url, { method: "GET" });
      if (response.statusCode >= 400) {
        await response.body.text();
        return null;
      }
      const text = await response.body.text();
      const doc: unknown = JSON.parse(text);
      return doc !== null && typeof doc === "object" && !Array.isArray(doc) ? (doc as OpenApiSpec) : null;
    } catch (error) {
      apiLogger.debug({ error: error instanceof Error ? error.message : String(error) }, "Original OpenAPI fetch failed");
      return null;
    }
  }

  /**
   * Update an existing custom connector
   */
  async updateCustomConnector(connectorId: string, definition: CustomConnectorDefinition): Promise<CustomConnector> {
    validateGraphId(connectorId, "connector");
    apiLogger.debug({ connectorId }, "Updating custom connector");

    const url = this.buildUrl(`/providers/Microsoft.PowerApps/apis/${connectorId}`, {
      $filter: `environment eq '${escapeOData(this.environmentId)}'`,
    });
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "PATCH",
        headers,
        body: JSON.stringify(serializeConnectorDefinition(definition, this.environmentId)),
      });
      // The platform acknowledges an update with an empty body (verified live:
      // the change lands, the response carries nothing). Read the connector
      // back so callers get the stored result rather than a parse error.
      const acknowledged = await this.handleResponse<CustomConnector | null>(response, connectorId, {
        allowEmpty: true,
      });
      const connector = acknowledged ?? (await this.getCustomConnector(connectorId));
      apiLogger.info(
        { connectorId, displayName: connector.properties.displayName },
        "Updated custom connector"
      );
      return connector;
    }, `Update custom connector ${connectorId}`);
  }

  /**
   * Delete a custom connector
   */
  async deleteCustomConnector(connectorId: string): Promise<void> {
    validateGraphId(connectorId, "connector");
    apiLogger.debug({ connectorId }, "Deleting custom connector");

    const url = this.buildUrl(`/providers/Microsoft.PowerApps/apis/${connectorId}`, {
      $filter: `environment eq '${escapeOData(this.environmentId)}'`,
    });
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "DELETE", headers });
      if (response.statusCode !== 200 && response.statusCode !== 204) {
        await this.handleResponse(response, connectorId);
      } else {
        await response.body.text(); // Consume body to prevent connection pool leak
      }
      apiLogger.info({ connectorId }, "Deleted custom connector");
    }, `Delete custom connector ${connectorId}`);
  }
}

// ==========================================================================
// Custom Connector Types
// ==========================================================================

export interface CustomConnector {
  name: string;
  id: string;
  type: string;
  properties: {
    displayName: string;
    description?: string;
    iconUri?: string;
    iconBrandColor?: string;
    contact?: { name?: string; email?: string; url?: string };
    license?: { name?: string; url?: string };
    apiEnvironment?: string;
    isCustomApi?: boolean;
    connectionParameters?: Record<string, unknown>;
    swagger?: SwaggerDefinition;
    createdTime?: string;
    changedTime?: string;
    createdBy?: { id?: string; displayName?: string; email?: string };
    capabilities?: string[];
    tier?: string;
    backendService?: { serviceUrl?: string };
    policyTemplateInstances?: unknown[];
    /** SAS links to the OpenAPI document as uploaded (`originalSwaggerUrl`) and as the platform rewrote it. */
    apiDefinitions?: { originalSwaggerUrl?: string; modifiedSwaggerUrl?: string };
  };
}

/**
 * Shape a connector definition the way the WRITE side of the API wants it.
 *
 * The API is asymmetric: GET returns the OpenAPI document under
 * `properties.swagger`, but POST/PUT insist on `properties.openApiDefinition`
 * ("Either 'OpenApiDefinition' or 'AppDefinitionUrl' must be specified") and
 * reject unknown members outright ("Could not find member 'apiType'"). Both
 * were learned from live rejections — every create had been failing.
 */
export function serializeConnectorDefinition(
  definition: CustomConnectorDefinition,
  environmentId: string
): Record<string, unknown> {
  const { swagger, ...rest } = definition.properties;
  return {
    properties: {
      ...rest,
      ...(swagger !== undefined ? { openApiDefinition: swagger } : {}),
      environment: { name: environmentId },
    },
  };
}

export interface CustomConnectorDefinition {
  properties: {
    /** Required on create. Must be ABSENT on update: the platform refuses `CannotUpdateApiDisplayName`. */
    displayName?: string;
    description?: string;
    iconBrandColor?: string;
    connectionParameters?: Record<string, ConnectionParameter>;
    swagger?: OpenApiSpec;
    backendService?: {
      serviceUrl: string;
    };
    policyTemplateInstances?: unknown[];
    capabilities?: string[];
  };
}

export interface ConnectionParameter {
  type: "string" | "securestring" | "oauthSetting";
  uiDefinition?: {
    displayName: string;
    description?: string;
    tooltip?: string;
    constraints?: {
      required?: string;
      hidden?: string;
    };
  };
  oAuthSettings?: {
    identityProvider: string;
    clientId?: string;
    /** Write-only: accepted on create/update, never returned by the service. */
    clientSecret?: string;
    redirectMode?: string;
    scopes?: string[];
    redirectUrl?: string;
    properties?: Record<string, unknown>;
    customParameters?: Record<string, unknown>;
  };
}

export interface OpenApiSpec {
  swagger?: string;
  openapi?: string;
  info: {
    title: string;
    description?: string;
    version: string;
    contact?: { name?: string; email?: string; url?: string };
  };
  host?: string;
  basePath?: string;
  schemes?: string[];
  servers?: Array<{ url: string; description?: string }>;
  consumes?: string[];
  produces?: string[];
  paths: Record<string, Record<string, OpenApiOperation>>;
  definitions?: Record<string, unknown>;
  components?: { schemas?: Record<string, unknown>; securitySchemes?: Record<string, unknown> };
  securityDefinitions?: Record<string, unknown>;
  security?: Array<Record<string, string[]>>;
}

export interface OpenApiOperation {
  operationId: string;
  summary?: string;
  description?: string;
  parameters?: OpenApiParameter[];
  requestBody?: unknown;
  responses: Record<string, unknown>;
  tags?: string[];
  "x-ms-visibility"?: "important" | "advanced" | "internal";
  "x-ms-trigger"?: "single" | "batch";
}

export interface OpenApiParameter {
  name: string;
  in: "query" | "header" | "path" | "body" | "formData";
  description?: string;
  required?: boolean;
  type?: string;
  format?: string;
  schema?: unknown;
  "x-ms-summary"?: string;
  "x-ms-visibility"?: "important" | "advanced" | "internal";
}
